import { Injectable, EventEmitter, NgZone } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import * as SignalR from '@microsoft/signalr';
import { firstValueFrom } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

import { AuthService } from '../../auth/auth/auth.service';
import { AppConfigService } from '../appconfig/appconfig.service';
import { AlertsService } from '../../chatstyle/alerts/alerts.service';

export interface OrgNotification {
  name: string;
  title: string;
  message: string;
  status: string;
  organizationId: string;
  timestamp: Date;
}

@Injectable({
  providedIn: 'root'
})
export class OrgNotificationService {
  private hubConnection: SignalR.HubConnection;
  private isConnected = false;
  private currentOrgId: string | null = null;

  public notificationReceived = new EventEmitter<OrgNotification>();

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private toastr: ToastrService,
    private alertsService: AlertsService,
    private ngZone: NgZone
  ) {}

  async connect(orgId: string): Promise<void> {
    if (this.isConnected && this.currentOrgId === orgId) return;

    if (this.isConnected) {
      await this.disconnect();
    }

    const signalrBaseUrl = AppConfigService.settings.get('signalrServer');
    if (!signalrBaseUrl) {
      console.warn('OrgNotificationService: signalrServer not configured');
      return;
    }

    const userId = this.authService.getClaims()?.sub;
    if (!userId) {
      console.error('OrgNotificationService: cannot connect, user sub claim is missing');
      return;
    }

    try {
      // Negotiate connection
      const headers = new HttpHeaders({ 'x-ms-signalr-userid': userId });
      const connectionInfo = await firstValueFrom(
        this.http.post<any>(`${signalrBaseUrl}/negotiate`, {}, { headers, withCredentials: false })
      );

      // Build hub connection
      this.hubConnection = new SignalR.HubConnectionBuilder()
        .withUrl(connectionInfo.url, {
          accessTokenFactory: () => connectionInfo.accessToken,
          withCredentials: false
        })
        .withAutomaticReconnect()
        .build();

      // Listen for org notifications
      this.hubConnection.on('orgNotification', (notification: OrgNotification) => {
        this.ngZone.run(() => this.handleNotification({ ...notification, timestamp: new Date(notification.timestamp) }));
      });

      await this.hubConnection.start();

      // Join org group before marking connected
      await this.joinOrgGroup(orgId, userId, signalrBaseUrl);

      this.isConnected = true;
      this.currentOrgId = orgId;

    } catch (error) {
      console.error('OrgNotificationService: failed to connect', error);
      if (this.hubConnection) {
        await this.hubConnection.stop().catch(() => {});
        this.hubConnection = null;
      }
      this.isConnected = false;
      this.currentOrgId = null;
      throw error;
    }
  }

  private async joinOrgGroup(orgId: string, userId: string, baseUrl: string): Promise<void> {
    const headers = new HttpHeaders({ 'x-ms-signalr-userid': userId });
    await firstValueFrom(
      this.http.post(`${baseUrl}/joinOrgGroup?orgId=${encodeURIComponent(orgId)}`, {}, { headers, withCredentials: false })
    );
  }

  private handleNotification(notification: OrgNotification): void {
    this.notificationReceived.emit(notification);

    // Show toastr
    const title = notification.title || 'Notification';
    const message = notification.message || '';

    if (notification.status === 'error') {
      this.toastr.error(message, title);
    } else if (notification.status === 'warning') {
      this.toastr.warning(message, title);
    } else {
      this.toastr.success(message, title);
    }

    // Resolve existing alerts since user has been notified, then refresh header
    this.alertsService.resolveAll().subscribe({
      error: (err) => console.warn('OrgNotificationService: resolveAll failed', err)
    });
  }

  async disconnect(): Promise<void> {
    if (this.hubConnection && this.isConnected) {
      await this.hubConnection.stop();
      this.isConnected = false;
      this.currentOrgId = null;
    }
  }
}
